import React from 'react';
import { Form } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import type { FieldValues, FieldPath } from 'react-hook-form';

export interface FormFieldProps<T extends FieldValues> {
  /** RHF 欄位路徑（支援 nested：`schedules.0.startTime`） */
  name: FieldPath<T>;
  label: React.ReactNode;
  /** 必填視覺標示（* + aria-required）— 實際驗證來自 zod schema */
  required?: boolean;
  help?: React.ReactNode;
  /** AntD 輸入元件，由 Controller 自動注入 value/onChange */
  control: React.ReactElement;
  /** 欄位 layout 輔助（例如 port 與 protocol 並列） */
  className?: string;
  /** 覆寫 Controller 傳給 control 元件的 props 名稱（預設 value/onChange） */
  valuePropName?: string;
}

/**
 * Schema-aware 欄位元件：
 * - 從 RHF context 取 control，自動 bind value/onChange
 * - 自動把 errors[name] 接到 AntD Form.Item 的 validateStatus / help
 * - 不做驗證本身，驗證由 zodResolver 集中處理
 */
export function FormField<T extends FieldValues>({
  name,
  label,
  required,
  help,
  control,
  className,
  valuePropName = 'value',
}: FormFieldProps<T>) {
  const { control: rhfControl, formState, getFieldState } = useFormContext<T>();
  const fieldError = getFieldState(name, formState).error;
  const errorMsg = fieldError?.message;

  return (
    <Form.Item
      label={label}
      required={required}
      validateStatus={errorMsg ? 'error' : undefined}
      help={errorMsg || help}
      className={className}
    >
      <Controller
        name={name}
        control={rhfControl}
        render={({ field }) => {
          const controlOnChange = (control.props as { onChange?: (...args: any[]) => void }).onChange;
          const injected: Record<string, unknown> = {
            ...field,
            [valuePropName]: (field as Record<string, unknown>)[valuePropName] ?? field.value,
            'aria-invalid': !!errorMsg,
            'aria-required': required || undefined,
          };
          // AntD Switch/Checkbox 需要 checked prop 而非 value
          if (valuePropName === 'checked') {
            injected.checked = !!field.value;
          }
          // Preserve an explicit control callback (for example, reloading a
          // brand-scoped form after Select changes) while RHF remains the
          // source of truth for the field value.
          injected.onChange = (...args: any[]) => {
            field.onChange(...args);
            controlOnChange?.(...args);
          };
          return React.cloneElement(control, injected);
        }}
      />
    </Form.Item>
  );
}
